import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import {
  Grid, Box, Typography, Button, Breadcrumbs, Link, Card, Chip, Divider, CircularProgress, Paper, styled, Table, TableBody, TableCell, TableContainer, TableRow,
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Science as ScienceIcon,
  Person as PersonIcon,
  MonitorHeart as MonitorHeartIcon,
  History as HistoryIcon,
  PregnantWoman as PregnantWomanIcon,
  WaterDrop as WaterDropIcon,
  Biotech as BiotechIcon,
  MedicalServices as MedicalServicesIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Bloodtype as BloodtypeIcon,
  EventNote as EventNoteIcon,
  Print as PrintIcon,
  VolunteerActivism as VolunteerActivismIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import SectionWrapper from './components/SectionWrapper';
import PatientInfoCard from './components/PatientInfoCard';
import VitalsForm from './components/VitalsForm';
import MedicalHistoryDisplay from './components/MedicalHistoryDisplay';
import DonorMedicalHistorySection from './components/DonorMedicalHistorySection';
import ObstetricHistoryDisplay from './components/ObstetricHistoryDisplay';
import MenstrualHistoryDisplay from './components/MenstrualHistoryDisplay';
import BloodTestsForm from './components/BloodTestsForm';
import ImportSurgicalHistoryDialog from './components/ImportSurgicalHistoryDialog';
import USGSection from './components/USGSection';
import SemenAnalysisSection from './components/SemenAnalysisSection';
import DFISection from './components/DFISection';
import SpouseBloodTestsSection from './components/SpouseBloodTestsSection';
import PlanAndCauseSection from './components/PlanAndCauseSection';
import DonorSection from './components/DonorSection';
import ProcedureInitiationPrint from './components/ProcedureInitiationPrint';
import irmHeader from 'assets/images/irm-header.png';

// Styled components for A4-like document design
const ProcedureInitiationContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '794px',
  minHeight: 'auto',
  margin: '0 auto',
  padding: '20px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: '12px',
  lineHeight: '1.4',
  color: '#333',
  '@media print': {
    boxShadow: 'none',
    margin: '0 !important',
    padding: '5mm 5mm !important',
    paddingBottom: '10mm !important',
    marginBottom: '0px !important',
    minHeight: 'auto',
    pageBreakInside: 'auto',
    width: '100% !important',
    maxWidth: 'none !important',
    backgroundColor: '#ffffff !important',
  },
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '15px',
  paddingBottom: '10px',
  borderBottom: '2px solid #00798E',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 'bold',
  color: '#00798E',
  marginBottom: '4px',
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: '#7b809a',
  fontStyle: 'italic',
}));

const SectionHeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '15px',
  justifyContent: 'center',
}));

const SectionTitleUnderline = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 'bold',
  color: '#00798E',
  paddingBottom: '8px',
  borderBottom: '2px solid #00798E',
  display: 'inline-block',
}));

const WifeHeaderGrid = styled(Grid)(({ theme }) => ({
  marginBottom: '15px',
  backgroundColor: '#f8f9fa',
  padding: '12px',
  borderRadius: '4px',
  border: '1px solid #e0e0e0',
}));

const SectionHeaderCircle = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '14px',
}));

const SectionHeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00798E',
}));

const DoneButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4caf50',
  color: 'white',
  textTransform: 'none',
  fontWeight: '600',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '11px',
  marginLeft: 'auto',
  '&:hover': {
    backgroundColor: '#45a049',
  },
}));

const HeaderImageSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '16px',
  '& img': {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
}));

const PatientInfoTableCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '15px',
  border: '1px solid #e0e0e0',
}));

const DataTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #e0e0e0',
    padding: '8px 12px',
  },
  '& .patient-info-label': {
    fontWeight: 'bold',
    color: '#128296',
    fontSize: '10px',
    backgroundColor: '#f0f8ff',
  },
  '& .patient-info-value': {
    fontSize: '11px',
    color: '#344767',
  },
}));

const GENERIC_FIELD_SLUGS = {
  patient_history: 'history-common-params-previous-history',
  family_history: 'history-common-params-family-history',
  allergic_to_drugs: 'history-common-params-allergic-to-drug',
  drug_medical_history: 'history-common-params-drug-medical-history',
  previous_infertility_treatment: 'history-common-params-prev-infertility-treatment',
  notes: 'history-common-params-note',
};

function ProcedureInitiation() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';
  const primaryHoverColor = theme.palette.logo?.state || '#005f75';
  const textColor = theme.palette.text?.main || '#344767';
  const secondaryColor = theme.palette.secondary?.main || '#7b809a';

  const { hasPermission } = usePermissions();
  const canSave = hasPermission('v1-ivf-create-draft-procedure');
  const canEdit = hasPermission('v1-ivf-update-procedure');
  const canDelete = hasPermission('v1-ivf-delete-procedure');

  // Form for wife vitals
  const { control, handleSubmit, watch, setValue, reset, trigger, getValues, formState: { errors } } = useForm({
    defaultValues: {
      weight: '',
      height: '',
      bmi: '',
      bp_systolic: '',
      bp_diastolic: '',
      pulse: '',
    },
  });

  // Form for husband vitals
  const { control: hControl, watch: hWatch, setValue: hSetValue, reset: hReset, trigger: hTrigger, getValues: hGetValues, formState: { errors: hErrors } } = useForm({
    defaultValues: {
      weight: '',
      height: '',
      bmi: '',
      bp_systolic: '',
      bp_diastolic: '',
      pulse: '',
    },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vitalsSaving, setVitalsSaving] = useState(false);
  const [husbandVitalsSaving, setHusbandVitalsSaving] = useState(false);
  const [storedProcedureHistories, setStoredProcedureHistories] = useState([]);
  const [storedProcedureUsgs, setStoredProcedureUsgs] = useState([]);
  const [procedureData, setProcedureData] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [spouseInfo, setSpouseInfo] = useState(null);
  const [genericHistory, setGenericHistory] = useState(null);
  const [surgicalHistory, setSurgicalHistory] = useState([]);
  const [obstetricHistory, setObstetricHistory] = useState(null);
  const [miscarriageHistories, setMiscarriageHistories] = useState([]);
  const [menstrualHistories, setMenstrualHistories] = useState([]);
  const [bloodGroup, setBloodGroup] = useState(null);
  const [bloodParams, setBloodParams] = useState(null);
  const [existingProcedureContacts, setExistingProcedureContacts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [selectedSurgicalVisit, setSelectedSurgicalVisit] = useState('ALL_VISITS');
  const [importingSurgicalHistory, setImportingSurgicalHistory] = useState(false);
  const [showWifeSurgicalImportDialog, setShowWifeSurgicalImportDialog] = useState(false);
  const [husbandGenericHistory, setHusbandGenericHistory] = useState(null);
  const [husbandSurgicalHistory, setHusbandSurgicalHistory] = useState([]);
  const [husbandVisits, setHusbandVisits] = useState([]);
  const [selectedHusbandSurgicalVisit, setSelectedHusbandSurgicalVisit] = useState('ALL_VISITS');
  const [importingHusbandSurgicalHistory, setImportingHusbandSurgicalHistory] = useState(false);
  const [showHusbandSurgicalImportDialog, setShowHusbandSurgicalImportDialog] = useState(false);
  const [husbandSemenAnalysis, setHusbandSemenAnalysis] = useState([]);
  const [husbandDfiHistory, setHusbandDfiHistory] = useState([]);
  const [maleInvestigationParams, setMaleInvestigationParams] = useState(null);
  const [husbandContactHistories, setHusbandContactHistories] = useState([]);
  const [surgicalHistoryOptions, setSurgicalHistoryOptions] = useState([]);
  const [procedureDetail, setProcedureDetail]                 = useState(null);
  // latestDetail tracks any in-session updates from PlanAndCauseSection
  const [latestDetail, setLatestDetail]                       = useState(null);

  // Donor female history state
  const [donorFemaleContactId, setDonorFemaleContactId] = useState(null);
  const [donorFemaleInfo, setDonorFemaleInfo] = useState(null);
  const [donorFemaleGenericHistory, setDonorFemaleGenericHistory] = useState(null);
  const [donorFemaleSurgicalHistory, setDonorFemaleSurgicalHistory] = useState([]);
  const [donorFemaleObstetricHistory, setDonorFemaleObstetricHistory] = useState(null);
  const [donorFemaleMiscarriageHistories, setDonorFemaleMiscarriageHistories] = useState([]);
  const [donorFemaleMenstrualHistories, setDonorFemaleMenstrualHistories] = useState([]);
  const [donorFemaleContactHistories, setDonorFemaleContactHistories] = useState([]);

  // Donor male history state
  const [donorMaleContactId, setDonorMaleContactId] = useState(null);
  const [donorMaleInfo, setDonorMaleInfo] = useState(null);
  const [donorMaleGenericHistory, setDonorMaleGenericHistory] = useState(null);
  const [donorMaleSurgicalHistory, setDonorMaleSurgicalHistory] = useState([]);
  const [donorMaleSemenAnalysis, setDonorMaleSemenAnalysis] = useState([]);
  const [donorMaleDfiHistory, setDonorMaleDfiHistory] = useState([]);
  const [donorMaleContactHistories, setDonorMaleContactHistories] = useState([]);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `IVF_Initiation_${patientInfo?.external_code || procedureId}`,
    pageStyle: `
      @page { size: A4; margin: 8mm; }
      body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    `,
  });

  // Extract menstrual history from contact histories
  const extractMenstrualHistory = useCallback((histories) => {
    const menstrualRecords = (histories || []).filter(h => {
      const slug = h.param_group_member?.slug;
      return slug && slug.startsWith('menstrual-history-params-');
    });

    if (menstrualRecords.length === 0) return [];

    const groupedRecords = {};
    menstrualRecords.forEach(record => {
      const groupKey = record.private_note || 'default';
      if (!groupedRecords[groupKey]) groupedRecords[groupKey] = {};

      const slug = record.param_group_member?.slug;
      const fieldMap = {
        'menstrual-history-params-lmp': 'lmp',
        'menstrual-history-params-menarche': 'menarche',
        'menstrual-history-params-cycle-length': 'cycleLength',
        'menstrual-history-params-duration': 'duration',
        'menstrual-history-params-flow': 'flow',
        'menstrual-history-params-pain': 'pain',
        'menstrual-history-params-others': 'other',
      };

      const fieldName = fieldMap[slug];
      if (fieldName) {
        groupedRecords[groupKey][fieldName] = record.value && record.value !== 'NA' ? record.value : '';
      }
    });

    return Object.values(groupedRecords).map(group => ({
      lmp: group.lmp || '', menarche: group.menarche || '',
      cycleLength: group.cycleLength || '', duration: group.duration || '',
      flow: group.flow || '', pain: group.pain || '', other: group.other || '',
    }));
  }, []);

  // Extract generic history from contact histories
  const extractGenericHistory = useCallback((histories) => {
    const formData = {};
    (histories || []).forEach(historyItem => {
      if (historyItem.param_group_member?.slug) {
        Object.entries(GENERIC_FIELD_SLUGS).forEach(([fieldName, fieldSlug]) => {
          if (fieldSlug === historyItem.param_group_member.slug) {
            formData[fieldName] = historyItem.value || '';
          }
        });
      }
    });
    return Object.keys(formData).length > 0 ? formData : null;
  }, []);

  // Extract surgical history from contact histories
  const extractSurgicalHistory = useCallback((histories) => {
    return (histories || [])
      .filter(h => h.param_group_member?.slug === 'primary-history-common-params-surgical-history')
      .map(h => ({
        id: h.id,
        surgery: h.param_group_member_option?.value || h.value || '',
        notes: h.remarks || '',
        history_date: h.history_date || '',
        value: h.value || '',
        contact_id: h.irm_contacts_id,
        param_group_id: h.irm_param_groups_id,
        param_group_member_id: h.irm_param_group_members_id,
        param_group_member_option_id: h.irm_param_group_member_options_id || null,
        visit_no: h.visit_no || h.visit?.visit_no || null,
      }));
  }, []);

  // Extract surgical history from procedure histories (already attached to procedure)
  const extractProcedureSurgicalHistory = useCallback((procedureHistories, contactId) => {
    return (procedureHistories || [])
      .filter(h => {
        // Filter by surgical history slug and contact ID
        const isSurgical = h.param_group_member?.slug === 'primary-history-common-params-surgical-history';
        const isForContact = h.irm_contacts_id === contactId;
        return isSurgical && isForContact;
      })
      .map(h => ({
        id: h.irm_contact_histories_id || h.id,
        surgery: h.param_group_member_option?.value || h.value || '',
        notes: h.remarks || '',
        history_date: h.history_date || '',
        value: h.value || '',
        contact_id: h.irm_contacts_id,
        param_group_id: h.irm_param_groups_id,
        param_group_member_id: h.irm_param_group_members_id,
        param_group_member_option_id: h.irm_param_group_member_options_id || null,
        visit_no: null, // Procedure histories don't have visit info
      }));
  }, []);

  // Extract semen analysis from contact histories
  const extractSemenAnalysis = useCallback((histories) => {
    return (histories || []).filter(
      h => h.param_group_member?.slug === 'male-investigation-params-semen-analysis',
    );
  }, []);

  // Extract DFI from contact histories
  const extractDfiHistory = useCallback((histories) => {
    return (histories || []).filter(
      h => h.param_group_member?.slug === 'male-investigation-params-dfi',
    );
  }, []);

  // Extract blood group from contact histories
  const extractBloodGroup = useCallback((histories) => {
    const bloodGroupEntry = (histories || []).find(h => {
      const slug = h.param_group_member?.slug || '';
      return slug.includes('blood-group') || slug.includes('aborh') || slug.includes('blood_group');
    });
    return bloodGroupEntry?.value || null;
  }, []);

  const normalizeVisits = useCallback((visitList) => {
    const safeList = Array.isArray(visitList) ? visitList : [];
    const seen = new Set();

    return safeList
      .map((visit) => ({
        ...visit,
        visit_no: Number(visit?.visit_no),
      }))
      .filter((visit) => Number.isFinite(visit.visit_no) && visit.visit_no > 0)
      .sort((a, b) => b.visit_no - a.visit_no)
      .filter((visit) => {
        if (seen.has(visit.visit_no)) return false;
        seen.add(visit.visit_no);
        return true;
      });
  }, []);

  // Search for latest history data across visit numbers (descending)
  const findLatestHistoryData = useCallback(async (contactId, allVisits) => {
    // First try without visitNo filter to get ALL histories
    try {
      const response = await api.opd.getContactHistory(contactId);
      const contactHistories = response?.data?.contact_histories || [];
      const obstetric = response?.data?.obsteric_history || null;
      const miscarriages = response?.data?.miscarriage_histories || [];

      if (contactHistories.length > 0 || obstetric || miscarriages.length > 0) {
        return { contactHistories, obstetric, miscarriages, found: true };
      }
    } catch (err) {
      console.error('Error fetching all histories:', err);
    }

    // If nothing found without filter, search by visit number descending
    if (allVisits && allVisits.length > 0) {
      const sortedVisits = [...allVisits].sort((a, b) => b.visit_no - a.visit_no);
      for (const visit of sortedVisits) {
        try {
          const response = await api.opd.getContactHistory(contactId, { visitNo: visit.visit_no });
          const contactHistories = response?.data?.contact_histories || [];
          const obstetric = response?.data?.obsteric_history || null;
          const miscarriages = response?.data?.miscarriage_histories || [];

          if (contactHistories.length > 0 || obstetric || miscarriages.length > 0) {
            return { contactHistories, obstetric, miscarriages, found: true };
          }
        } catch (err) {
          console.error(`Error fetching history for visit ${visit.visit_no}:`, err);
        }
      }
    }

    return { contactHistories: [], obstetric: null, miscarriages: [], found: false };
  }, []);

  // Load all initiation data
  useEffect(() => {
    const loadInitiationData = async () => {
      setLoading(true);
      try {
        // Step 1: Get procedure initiation data from backend
        const initiationResponse = await api.ivf.getInitiationData(procedureId);

        if (!initiationResponse || initiationResponse.flag !== 1) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load procedure data' });
          navigate('/ivf');
          return;
        }

        const { procedure, patientData, spouseData, procedureContacts, procedureHistories } = initiationResponse.data;
        setProcedureData(procedure);
        setExistingProcedureContacts(procedureContacts || []);
        setStoredProcedureHistories(procedureHistories || []);
        setStoredProcedureUsgs(initiationResponse.data.procedureUsgs || []);
        setProcedureDetail(initiationResponse.data.procedureDetail || null);

        // Step 2: Extract patient info from contacts service response
        const detailedContact = patientData?.detailedContact ?? patientData?.data ?? patientData;
        const contactRecord = detailedContact?.contact ?? detailedContact;
        const phones = detailedContact?.phones ?? patientData?.phones ?? [];
        const coupleInfo = detailedContact?.patientCouple ?? detailedContact?.spouseCouple ?? detailedContact?.couple_info;

        const patientInfoObj = {
          id: contactRecord?.id || procedure.irm_contacts_id,
          full_name: contactRecord?.full_name || `${contactRecord?.first_name || ''} ${contactRecord?.last_name || ''}`.trim(),
          first_name: contactRecord?.first_name || '',
          last_name: contactRecord?.last_name || '',
          date_of_birth: contactRecord?.date_of_birth,
          age: contactRecord?.age,
          occupation: contactRecord?.occupation,
          gender: contactRecord?.gender,
          email_address: contactRecord?.email_address,
          phone_number: phones?.[0]?.phone_number || '',
          marital_status: contactRecord?.marital_status,
          married_for: contactRecord?.married_for,
          trying_for: contactRecord?.trying_for,
          external_code: contactRecord?.external_code,
          couple_code: coupleInfo?.external_code,
        };
        setPatientInfo(patientInfoObj);

        // Step 3: Extract spouse info
        if (spouseData) {
          const spouseDetailed = spouseData?.detailedContact ?? spouseData?.data ?? spouseData;
          const spouseContact = spouseDetailed?.contact ?? spouseDetailed;
          const spousePhones = spouseDetailed?.phones ?? spouseData?.phones ?? [];
          setSpouseInfo({
            id: spouseContact?.id || procedure.irm_spouse_contacts_id,
            full_name: spouseContact?.full_name || `${spouseContact?.first_name || ''} ${spouseContact?.last_name || ''}`.trim(),
            first_name: spouseContact?.first_name || '',
            last_name: spouseContact?.last_name || '',
            date_of_birth: spouseContact?.date_of_birth,
            age: spouseContact?.age,
            occupation: spouseContact?.occupation,
            gender: spouseContact?.gender,
            email_address: spouseContact?.email_address || '',
            phone_number: spousePhones?.[0]?.phone_number || '',
            marital_status: spouseContact?.marital_status,
            married_for: spouseContact?.married_for,
            trying_for: spouseContact?.trying_for,
            external_code: spouseContact?.external_code,
          });
        }

        // Step 4: Pre-fill vitals if existing procedure contacts
        if (procedureContacts && procedureContacts.length > 0) {
          const wifeContact = procedureContacts.find(c => c.gender === 'F') || procedureContacts[0];
          if (wifeContact) {
            reset({
              weight: wifeContact.weight || '',
              height: wifeContact.height || '',
              bmi: wifeContact.bmi || '',
              bp_systolic: wifeContact.bp_systolic || '',
              bp_diastolic: wifeContact.bp_diastolic || '',
              pulse: wifeContact.pulse || '',
            });
          }
          const husbandContact = procedureContacts.find(c => c.gender === 'M');
          if (husbandContact) {
            hReset({
              weight: husbandContact.weight || '',
              height: husbandContact.height || '',
              bmi: husbandContact.bmi || '',
              bp_systolic: husbandContact.bp_systolic || '',
              bp_diastolic: husbandContact.bp_diastolic || '',
              pulse: husbandContact.pulse || '',
            });
          }
        }

        // Step 5: Fetch all visits for the patient
        let allVisits = [];
        try {
          const visitsResponse = await api.contacts.getAllVisitsByContact(patientInfoObj.id);
          allVisits = normalizeVisits(visitsResponse?.data);
          setVisits(allVisits);
        } catch (err) {
          console.error('Error loading visits:', err);
        }

        // Step 6: Fetch latest history data (search from latest visit down)
        const historyResult = await findLatestHistoryData(patientInfoObj.id, allVisits);

        if (historyResult.found) {
          const { contactHistories, obstetric, miscarriages } = historyResult;

          setGenericHistory(extractGenericHistory(contactHistories));
          
          // First check if there are surgical histories already attached to this procedure
          const procedureSurgicals = extractProcedureSurgicalHistory(procedureHistories, patientInfoObj.id);
          console.log(procedureSurgicals, procedureHistories, 'procedure surgicals');
          if (procedureSurgicals.length > 0) {
            setSurgicalHistory(procedureSurgicals);
          } else {
            // Fallback to contact histories if nothing attached to procedure yet
            setSurgicalHistory(extractSurgicalHistory(contactHistories));
          }
          
          setObstetricHistory(obstetric);
          setMiscarriageHistories(miscarriages);
          setMenstrualHistories(extractMenstrualHistory(contactHistories));

          // Extract blood group
          const bg = extractBloodGroup(contactHistories);
          if (bg) setBloodGroup(bg);
        }

        // Step 6b: Fetch husband's history data
        if (spouseData) {
          const spouseDetailed = spouseData?.detailedContact ?? spouseData?.data ?? spouseData;
          const spouseContact = spouseDetailed?.contact ?? spouseDetailed;
          const spouseContactId = spouseContact?.id || procedure.irm_spouse_contacts_id;
          if (spouseContactId) {
            try {
              const husbandVisitsResponse = await api.contacts.getAllVisitsByContact(spouseContactId);
              const husbandVisits = normalizeVisits(husbandVisitsResponse?.data);
              setHusbandVisits(husbandVisits);
              const husbandHistoryResult = await findLatestHistoryData(spouseContactId, husbandVisits);
              if (husbandHistoryResult.found) {
                const { contactHistories: hHistories } = husbandHistoryResult;
                setHusbandGenericHistory(extractGenericHistory(hHistories));
                
                // First check if there are surgical histories already attached to this procedure
                const husbandProcedureSurgicals = extractProcedureSurgicalHistory(procedureHistories, spouseContactId);
                if (husbandProcedureSurgicals.length > 0) {
                  setHusbandSurgicalHistory(husbandProcedureSurgicals);
                } else {
                  // Fallback to contact histories if nothing attached to procedure yet
                  setHusbandSurgicalHistory(extractSurgicalHistory(hHistories));
                }
                
                setHusbandSemenAnalysis(extractSemenAnalysis(hHistories));
                setHusbandDfiHistory(extractDfiHistory(hHistories));
                setHusbandContactHistories(hHistories);
              }
            } catch (err) {
              console.error('Error loading husband history:', err);
            }
          }
        }

        // If blood group not found from histories, check obstetric data blood group
        if (!bloodGroup && obstetricHistory) {
          // Blood group might be in procedure contacts or patient details
        }

        // Step 7: Fetch blood investigation params for the form
        try {
          const paramsResponse = await api.opd.getHistoryParams();
          if (paramsResponse?.data?.blood_investigation_params) {
            setBloodParams(paramsResponse.data.blood_investigation_params);
          }
          if (paramsResponse?.data?.male_investigation_params) {
            setMaleInvestigationParams(paramsResponse.data.male_investigation_params);
          }
          // Extract surgical history options
          if (paramsResponse?.data?.primary_history_params?.param_group_members) {
            const surgicalHistory = paramsResponse.data.primary_history_params.param_group_members.find(
              (member) => member.slug === 'primary-history-common-params-surgical-history'
            );
            if (surgicalHistory?.param_group_member_options) {
              const surgicalOptions = surgicalHistory.param_group_member_options.map((option) => ({
                value: option.option_value,
                label: option.option_value,
                param_group_member_id: surgicalHistory.id,
                param_group_member_option_id: option.id,
                param_group_id: surgicalHistory.irm_param_groups_id,
              }));
              setSurgicalHistoryOptions(surgicalOptions);
            }
          }
        } catch (err) {
          console.error('Error loading blood params:', err);
        }

      } catch (error) {
        console.error('Error loading initiation data:', error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load procedure initiation data' });
      } finally {
        setLoading(false);
      }
    };

    if (procedureId) {
      loadInitiationData();
    }
  }, [procedureId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle vitals-only save
  const handleSaveVitals = async () => {
    const valid = await trigger(['weight', 'height', 'bp_systolic', 'bp_diastolic', 'pulse']);
    if (!valid) return;

    if (!procedureData || !patientInfo) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Missing procedure or patient data' });
      return;
    }

    setVitalsSaving(true);
    try {
      const formData = getValues();
      const contactPayload = {
        irm_contacts_id: patientInfo.id,
        first_name: patientInfo.first_name,
        middle_name: null,
        last_name: patientInfo.last_name,
        gender: patientInfo.gender || 'F',
        date_of_birth: patientInfo.date_of_birth,
        age: patientInfo.age,
        occupation: patientInfo.occupation,
        married_for: patientInfo.married_for,
        infertility_type: obstetricHistory?.infetrtility_type || null,
        infertility_type_remarks: obstetricHistory?.remarks || null,
        blood_group: bloodGroup,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        bmi: formData.bmi ? parseFloat(formData.bmi) : null,
        bp_systolic: formData.bp_systolic ? parseInt(formData.bp_systolic) : null,
        bp_diastolic: formData.bp_diastolic ? parseInt(formData.bp_diastolic) : null,
        pulse: formData.pulse ? parseInt(formData.pulse) : null,
      };
      await api.ivf.saveProcedureContact(procedureId, contactPayload);
      Swal.fire({
        icon: 'success',
        title: 'Vitals Saved',
        text: 'Today\'s vitals have been saved successfully.',
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error saving vitals:', error);
      Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message || 'Failed to save vitals.' });
    } finally {
      setVitalsSaving(false);
    }
  };

  // Handle husband vitals save
  const handleSaveHusbandVitals = async () => {
    const valid = await hTrigger(['weight', 'height', 'bp_systolic', 'bp_diastolic', 'pulse']);
    if (!valid) return;

    if (!procedureData || !spouseInfo) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Missing procedure or husband data' });
      return;
    }

    setHusbandVitalsSaving(true);
    try {
      const formData = hGetValues();
      const contactPayload = {
        irm_contacts_id: spouseInfo.id,
        first_name: spouseInfo.first_name,
        middle_name: null,
        last_name: spouseInfo.last_name,
        gender: spouseInfo.gender || 'M',
        date_of_birth: spouseInfo.date_of_birth,
        age: spouseInfo.age,
        occupation: spouseInfo.occupation,
        married_for: spouseInfo.married_for,
        infertility_type: null,
        infertility_type_remarks: null,
        blood_group: null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        bmi: formData.bmi ? parseFloat(formData.bmi) : null,
        bp_systolic: formData.bp_systolic ? parseInt(formData.bp_systolic) : null,
        bp_diastolic: formData.bp_diastolic ? parseInt(formData.bp_diastolic) : null,
        pulse: formData.pulse ? parseInt(formData.pulse) : null,
      };
      await api.ivf.saveProcedureContact(procedureId, contactPayload);
      Swal.fire({
        icon: 'success',
        title: 'Vitals Saved',
        text: 'Husband\'s vitals have been saved successfully.',
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error saving husband vitals:', error);
      Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message || 'Failed to save vitals.' });
    } finally {
      setHusbandVitalsSaving(false);
    }
  };

  // Re-fetch husband blood histories after a save
  const handleSpouseBloodSaved = useCallback(async () => {
    if (!spouseInfo?.id) return;
    try {
      const response = await api.opd.getContactHistory(spouseInfo.id);
      setHusbandContactHistories(response?.data?.contact_histories || []);
    } catch (err) {
      console.error('Error refreshing husband blood histories:', err);
    }
  }, [spouseInfo?.id]);

  // Re-fetch husband semen analysis after a new entry is saved
  const handleSemenAnalysisSaved = useCallback(async () => {
    if (!spouseInfo?.id) return;
    try {
      const response = await api.opd.getContactHistory(spouseInfo.id);
      const histories = response?.data?.contact_histories || [];
      setHusbandSemenAnalysis(extractSemenAnalysis(histories));
    } catch (err) {
      console.error('Error refreshing husband semen analysis:', err);
    }
  }, [spouseInfo?.id, extractSemenAnalysis]);

  // Re-fetch DFI history after a new entry is saved
  const handleDfiSaved = useCallback(async () => {
    if (!spouseInfo?.id) return;
    try {
      const response = await api.opd.getContactHistory(spouseInfo.id);
      const histories = response?.data?.contact_histories || [];
      setHusbandDfiHistory(extractDfiHistory(histories));
    } catch (err) {
      console.error('Error refreshing DFI history:', err);
    }
  }, [spouseInfo?.id, extractDfiHistory]);

  const handleWifeSurgicalImport = useCallback(async (selectedRows, selectedVisit) => {
    if (!Array.isArray(selectedRows) || selectedRows.length === 0) return;

    setImportingSurgicalHistory(true);
    try {
      // Filter out already attached histories
      const newHistories = selectedRows.filter(row => {
        return !storedProcedureHistories.some(
          h => h.irm_param_group_members_id === row.param_group_member_id 
            && h.irm_contact_histories_id === row.id
        );
      });

      if (newHistories.length > 0) {
        // Prepare payload array for batch save
        const procedurePayload = newHistories.map(row => ({
          irm_procedures_id: procedureId,
          irm_contacts_id: patientInfo.id,
          irm_param_groups_id: row.param_group_id,
          irm_param_group_members_id: row.param_group_member_id,
          irm_param_group_member_options_id: row.param_group_member_option_id,
          irm_contact_histories_id: row.id,
          value: row.value || row.surgery,
          remarks: row.notes || '',
          history_date: row.history_date || null,
        }));

        // Save all histories in one request
        await api.ivf.saveProcedureHistories(procedureId, procedurePayload);
      }

      // Refresh procedure histories
      const initiationResponse = await api.ivf.getInitiationData(procedureId);
      setStoredProcedureHistories(initiationResponse.data.procedureHistories || []);

      // Update displayed surgical histories to show all procedure-attached ones
      const updatedProcedureSurgicals = extractProcedureSurgicalHistory(
        initiationResponse.data.procedureHistories || [],
        patientInfo.id
      );
      setSurgicalHistory(updatedProcedureSurgicals);

      setSelectedSurgicalVisit(selectedVisit || 'ALL_VISITS');

      Swal.fire({
        icon: 'success',
        title: 'Imported & Saved',
        text: `${selectedRows.length} surgical record${selectedRows.length > 1 ? 's' : ''} saved to procedure.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error saving surgical histories:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.message || 'Failed to save surgical histories.',
      });
    } finally {
      setImportingSurgicalHistory(false);
    }
  }, [procedureId, patientInfo?.id, storedProcedureHistories, extractProcedureSurgicalHistory]);

  const handleHusbandSurgicalImport = useCallback(async (selectedRows, selectedVisit) => {
    if (!Array.isArray(selectedRows) || selectedRows.length === 0) return;

    setImportingHusbandSurgicalHistory(true);
    try {
      // Filter out already attached histories
      const newHistories = selectedRows.filter(row => {
        return !storedProcedureHistories.some(
          h => h.irm_param_group_members_id === row.param_group_member_id 
            && h.irm_contact_histories_id === row.id
        );
      });

      if (newHistories.length > 0) {
        // Prepare payload array for batch save
        const procedurePayload = newHistories.map(row => ({
          irm_procedures_id: procedureId,
          irm_contacts_id: spouseInfo.id,
          irm_param_groups_id: row.param_group_id,
          irm_param_group_members_id: row.param_group_member_id,
          irm_param_group_member_options_id: row.param_group_member_option_id,
          irm_contact_histories_id: row.id,
          value: row.value || row.surgery,
          remarks: row.notes || '',
          history_date: row.history_date || null,
        }));

        // Save all histories in one request
        await api.ivf.saveProcedureHistories(procedureId, procedurePayload);
      }

      // Refresh procedure histories
      const initiationResponse = await api.ivf.getInitiationData(procedureId);
      setStoredProcedureHistories(initiationResponse.data.procedureHistories || []);

      // Update displayed surgical histories to show all procedure-attached ones
      const updatedHusbandProcedureSurgicals = extractProcedureSurgicalHistory(
        initiationResponse.data.procedureHistories || [],
        spouseInfo.id
      );
      setHusbandSurgicalHistory(updatedHusbandProcedureSurgicals);

      setSelectedHusbandSurgicalVisit(selectedVisit || 'ALL_VISITS');

      Swal.fire({
        icon: 'success',
        title: 'Imported & Saved',
        text: `${selectedRows.length} husband surgical record${selectedRows.length > 1 ? 's' : ''} saved to procedure.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error saving husband surgical histories:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.message || 'Failed to save husband surgical histories.',
      });
    } finally {
      setImportingHusbandSurgicalHistory(false);
    }
  }, [procedureId, spouseInfo?.id, storedProcedureHistories, extractProcedureSurgicalHistory]);

  const handleImportPatientSurgicalHistory = useCallback(() => {
    if (!patientInfo?.id) return;
    setShowWifeSurgicalImportDialog(true);
  }, [patientInfo?.id]);

  const handleImportHusbandSurgicalHistory = useCallback(() => {
    if (!spouseInfo?.id) return;
    setShowHusbandSurgicalImportDialog(true);
  }, [spouseInfo?.id]);

  // Fetch and set female donor history (called when donor is attached/detached/loaded)
  const handleDonorFemaleChange = useCallback(async (contactId, donorInfo) => {
    setDonorFemaleContactId(contactId || null);
    if (donorInfo !== undefined) {
      setDonorFemaleInfo(donorInfo || null);
    }
    if (!contactId) {
      setDonorFemaleInfo(null);
      setDonorFemaleGenericHistory(null);
      setDonorFemaleSurgicalHistory([]);
      setDonorFemaleObstetricHistory(null);
      setDonorFemaleMiscarriageHistories([]);
      setDonorFemaleMenstrualHistories([]);
      setDonorFemaleContactHistories([]);
      return;
    }
    try {
      let donorVisits = [];
      try {
        const visitsResponse = await api.contacts.getAllVisitsByContact(contactId);
        donorVisits = visitsResponse?.data || [];
      } catch (visitErr) {
        console.error('Error loading female donor visits:', visitErr);
      }

      const historyResult = await findLatestHistoryData(contactId, donorVisits);
      const histories = historyResult?.contactHistories || [];
      const obstetric = historyResult?.obstetric || null;
      const miscarriages = historyResult?.miscarriages || [];

      setDonorFemaleGenericHistory(extractGenericHistory(histories));
      
      // First check if there are surgical histories already attached to this procedure
      const donorFemaleProcedureSurgicals = extractProcedureSurgicalHistory(storedProcedureHistories, contactId);
      if (donorFemaleProcedureSurgicals.length > 0) {
        setDonorFemaleSurgicalHistory(donorFemaleProcedureSurgicals);
      } else {
        // Fallback to contact histories if nothing attached to procedure yet
        setDonorFemaleSurgicalHistory(extractSurgicalHistory(histories));
      }
      
      setDonorFemaleObstetricHistory(obstetric);
      setDonorFemaleMiscarriageHistories(miscarriages);
      setDonorFemaleMenstrualHistories(extractMenstrualHistory(histories));
      setDonorFemaleContactHistories(histories);
    } catch (err) {
      console.error('Error loading female donor history:', err);
    }
  }, [extractGenericHistory, extractSurgicalHistory, extractMenstrualHistory, findLatestHistoryData, extractProcedureSurgicalHistory, storedProcedureHistories]);

  // Fetch and set male donor history (called when donor is attached/detached/loaded)
  const handleDonorMaleChange = useCallback(async (contactId, donorInfo) => {
    setDonorMaleContactId(contactId || null);
    if (donorInfo !== undefined) {
      setDonorMaleInfo(donorInfo || null);
    }
    if (!contactId) {
      setDonorMaleInfo(null);
      setDonorMaleGenericHistory(null);
      setDonorMaleSurgicalHistory([]);
      setDonorMaleSemenAnalysis([]);
      setDonorMaleDfiHistory([]);
      setDonorMaleContactHistories([]);
      return;
    }
    try {
      let donorVisits = [];
      try {
        const visitsResponse = await api.contacts.getAllVisitsByContact(contactId);
        donorVisits = visitsResponse?.data || [];
      } catch (visitErr) {
        console.error('Error loading male donor visits:', visitErr);
      }

      const historyResult = await findLatestHistoryData(contactId, donorVisits);
      const histories = historyResult?.contactHistories || [];

      setDonorMaleGenericHistory(extractGenericHistory(histories));
      
      // First check if there are surgical histories already attached to this procedure
      const donorMaleProcedureSurgicals = extractProcedureSurgicalHistory(storedProcedureHistories, contactId);
      if (donorMaleProcedureSurgicals.length > 0) {
        setDonorMaleSurgicalHistory(donorMaleProcedureSurgicals);
      } else {
        // Fallback to contact histories if nothing attached to procedure yet
        setDonorMaleSurgicalHistory(extractSurgicalHistory(histories));
      }
      
      setDonorMaleSemenAnalysis(extractSemenAnalysis(histories));
      setDonorMaleDfiHistory(extractDfiHistory(histories));
      setDonorMaleContactHistories(histories);
    } catch (err) {
      console.error('Error loading male donor history:', err);
    }
  }, [extractGenericHistory, extractSurgicalHistory, extractSemenAnalysis, extractDfiHistory, findLatestHistoryData, extractProcedureSurgicalHistory, storedProcedureHistories]);

  // Handle form submission
  const onSubmit = async (formData) => {
    if (!procedureData || !patientInfo) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Missing procedure or patient data' });
      return;
    }

    setSaving(true);
    try {
      const promises = [];

      // 1. Save procedure contact (vitals + patient info snapshot)
      const contactPayload = {
        irm_contacts_id: patientInfo.id,
        first_name: patientInfo.first_name,
        middle_name: null,
        last_name: patientInfo.last_name,
        gender: patientInfo.gender || 'F',
        date_of_birth: patientInfo.date_of_birth,
        age: patientInfo.age,
        occupation: patientInfo.occupation,
        married_for: patientInfo.married_for,
        infertility_type: obstetricHistory?.infetrtility_type || null,
        infertility_type_remarks: obstetricHistory?.remarks || null,
        blood_group: bloodGroup,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        bmi: formData.bmi ? parseFloat(formData.bmi) : null,
        bp_systolic: formData.bp_systolic ? parseInt(formData.bp_systolic) : null,
        bp_diastolic: formData.bp_diastolic ? parseInt(formData.bp_diastolic) : null,
        pulse: formData.pulse ? parseInt(formData.pulse) : null,
      };
      promises.push(api.ivf.saveProcedureContact(procedureId, contactPayload));

      await Promise.all(promises);

      Swal.fire({
        icon: 'success',
        title: 'Saved Successfully',
        text: 'Procedure initiation data has been saved.',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error('Error saving initiation data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.message || 'Failed to save initiation data. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Check if we have history data to show sections
  const hasGenericHistory = genericHistory && Object.values(genericHistory).some(v => v && v.trim && v.trim() !== '');
  const hasSurgicalHistory = surgicalHistory && surgicalHistory.length > 0;
  const hasMedicalOrSurgical = hasGenericHistory || hasSurgicalHistory;
  const hasObstetricHistory = !!obstetricHistory || (miscarriageHistories && miscarriageHistories.length > 0);
  const hasMenstrualHistory = menstrualHistories && menstrualHistories.length > 0;
  const isIvfSelfProcedure = String(procedureData?.procedure_type || '').toUpperCase() === 'IVF'
    && String(procedureData?.procedure_sub_type || '').toUpperCase() === 'SELF';
  const shouldShowDonorSection = !!procedureData?.procedure_sub_type && !isIvfSelfProcedure;

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={true} />
        <MDBox p={3} sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={saving} />

      <MDBox p={3} sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {/* Breadcrumb Navigation */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: '#7b809a' }} />}
            sx={{
              backgroundColor: 'white', px: 2, py: 1.5, borderRadius: 1, border: '1px solid #e9ecef',
              '& .MuiBreadcrumbs-separator': { mx: 1 },
            }}
          >
            <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, '&:hover': { color: '#00798E' } }} onClick={() => navigate('/dashboard')}>
              <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} /> Dashboard
            </Link>
            <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, '&:hover': { color: '#00798E' } }} onClick={() => navigate('/ivf')}>
              <ScienceIcon sx={{ mr: 0.5, fontSize: 16 }} /> IVF Procedures
            </Link>
            <Typography sx={{ display: 'flex', alignItems: 'center', color: '#00798E', fontWeight: 700, fontSize: '0.875rem' }}>
              <MedicalServicesIcon sx={{ mr: 0.5, fontSize: 16 }} /> Procedure Initiation
            </Typography>
          </Breadcrumbs>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ivf')}
            sx={{ color: '#7b809a', textTransform: 'none', fontWeight: 500, '&:hover': { color: '#00798E' }, ml: 2 }}>
            Back
          </Button>
        </MDBox>

        {/* Main A4 Document Container */}
        <ProcedureInitiationContainer>
          {/* Header Image */}
          <HeaderImageSection>
            <img src={irmHeader} alt="IRM Header" />
          </HeaderImageSection>

          {/* Patient Information Table */}
          {patientInfo && (
            <PatientInfoTableCard>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '0' }}>
                <DataTable size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell className="patient-info-label" sx={{ width: '20%' }}>
                        Patient ID
                      </TableCell>
                      <TableCell className="patient-info-value" sx={{ width: '30%' }}>
                        {patientInfo?.external_code || patientInfo?.id || 'N/A'}
                      </TableCell>
                      <TableCell className="patient-info-label" sx={{ width: '20%' }}>
                        Procedure No
                      </TableCell>
                      <TableCell className="patient-info-value" sx={{ width: '30%' }}>
                        {procedureData?.external_code || procedureData?.procedure_type || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="patient-info-label">
                        Patient Name
                      </TableCell>
                      <TableCell className="patient-info-value">
                        {patientInfo?.full_name || `${patientInfo?.first_name || ''} ${patientInfo?.last_name || ''}`.trim() || 'N/A'}
                      </TableCell>
                      <TableCell className="patient-info-label">
                        Date of Birth
                      </TableCell>
                      <TableCell className="patient-info-value">
                        {patientInfo?.date_of_birth ? new Date(patientInfo.date_of_birth).toLocaleDateString('en-GB') : 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="patient-info-label">
                        Age
                      </TableCell>
                      <TableCell className="patient-info-value">
                        {patientInfo?.age || 'N/A'}
                      </TableCell>
                      <TableCell className="patient-info-label">
                        Contact
                      </TableCell>
                      <TableCell className="patient-info-value">
                        {patientInfo?.phone_number || patientInfo?.email_address || 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </DataTable>
              </TableContainer>
            </PatientInfoTableCard>
          )}

          {/* Page Header */}
          <PageHeader>
            <PageTitle>
              Initiation of {procedureData?.procedure_type || 'IVF'} Cycle
            </PageTitle>
          </PageHeader>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* ===== WIFE (FEMALE PATIENT) SECTION ===== */}
            <Box mb={3}>
              <SectionHeaderBox>
                <SectionHeaderCircle sx={{ backgroundColor: '#e91e8c' }}>
                  ♀
                </SectionHeaderCircle>
                <SectionHeaderTitle>
                  Wife
                </SectionHeaderTitle>
              </SectionHeaderBox>
              <SectionWrapper title="Wife Information" icon={PersonIcon} defaultExpanded={true} collapsible={false}>
                <PatientInfoCard
                  patientData={patientInfo}
                  infertilityData={obstetricHistory}
                  bloodGroup={bloodGroup}
                />
              </SectionWrapper>

              {/* Vitals */}
              <SectionWrapper title="Today's Vitals (Initiation Day)" icon={MonitorHeartIcon}>
                <VitalsForm
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  onSave={handleSaveVitals}
                  saving={vitalsSaving}
                  canSave={canSave}
                />
              </SectionWrapper>

              {/* Medical History */}
              <SectionWrapper title="Personal & Medical History" icon={HistoryIcon}>
                <MedicalHistoryDisplay
                  genericHistory={genericHistory}
                  surgicalHistory={surgicalHistory}
                  surgicalVisitOptions={visits}
                  selectedSurgicalVisit={selectedSurgicalVisit}
                  onSurgicalVisitChange={setSelectedSurgicalVisit}
                  onImportSurgicalHistory={handleImportPatientSurgicalHistory}
                  importingSurgicalHistory={importingSurgicalHistory}
                  visitLabel="Visit"
                  procedureHistories={storedProcedureHistories}
                  procedureId={procedureId}
                  contactId={patientInfo?.id}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  surgicalHistoryOptions={surgicalHistoryOptions}
                  onSaved={() => {
                    // Refresh procedure histories after edit/delete
                    api.ivf.getInitiationData(procedureId).then(res => {
                      if (res?.data?.procedureHistories) {
                        setStoredProcedureHistories(res.data.procedureHistories);
                        // Update displayed surgical histories
                        const updatedSurgicals = extractProcedureSurgicalHistory(
                          res.data.procedureHistories,
                          patientInfo.id
                        );
                        setSurgicalHistory(updatedSurgicals);
                      }
                    });
                  }}
                />
              </SectionWrapper>

              {/* Obstetric History */}
              {hasObstetricHistory && (
                <SectionWrapper title="Obstetric History" icon={PregnantWomanIcon} collapsible={false}>
                  <ObstetricHistoryDisplay
                    obstetricHistory={obstetricHistory}
                    miscarriageHistories={miscarriageHistories}
                  />
                </SectionWrapper>
              )}

              {/* Menstrual History */}
              {hasMenstrualHistory && (
                <SectionWrapper title="Latest Menstrual History" icon={WaterDropIcon}>
                  <MenstrualHistoryDisplay menstrualHistory={menstrualHistories} />
                </SectionWrapper>
              )}

              {/* Blood Tests Entry */}
              <SectionWrapper title="Blood" icon={BiotechIcon}>
                <BloodTestsForm
                  bloodParams={bloodParams}
                  patientInfo={patientInfo}
                  procedureId={procedureId}
                  existingHistories={storedProcedureHistories}
                  canSave={canSave}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              </SectionWrapper>

              {/* USG Section */}
              <SectionWrapper title="USG Section" icon={MedicalServicesIcon} defaultExpanded={true}>
                <USGSection
                    procedureId={procedureId}
                    existingUsgs={storedProcedureUsgs}
                    canSave={canSave}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onSaved={() => {
                      // Refresh procedure USGs after add/edit/delete so Print reflects the latest scans
                      api.ivf.getInitiationData(procedureId).then(res => {
                        if (res?.data?.procedureUsgs) {
                          setStoredProcedureUsgs(res.data.procedureUsgs);
                        }
                      });
                    }}
                  />
              </SectionWrapper>
            </Box>

            {/* ===== HUSBAND SECTION (Placeholder) ===== */}
            {spouseInfo && spouseInfo.id && (
              <Box mb={3}>
                <SectionHeaderBox>
                  <SectionHeaderCircle sx={{ backgroundColor: '#1976d2' }}>
                    ♂
                  </SectionHeaderCircle>
                  <SectionHeaderTitle>
                    Husband
                  </SectionHeaderTitle>
                </SectionHeaderBox>
                <SectionWrapper title="Husband Information" icon={PersonIcon} defaultExpanded={true} collapsible={false}>
                  <PatientInfoCard patientData={spouseInfo} infertilityData={null} bloodGroup={null} />
                </SectionWrapper>
                <SectionWrapper title="Today's Vitals (Initiation Day)" icon={MonitorHeartIcon}>
                  <VitalsForm
                    control={hControl}
                    watch={hWatch}
                    setValue={hSetValue}
                    errors={hErrors}
                    onSave={handleSaveHusbandVitals}
                    saving={husbandVitalsSaving}
                    canSave={canSave}
                  />
                </SectionWrapper>
                <SectionWrapper title="Personal & Medical History" icon={HistoryIcon}>
                  <MedicalHistoryDisplay
                    genericHistory={husbandGenericHistory}
                    surgicalHistory={husbandSurgicalHistory}
                    surgicalVisitOptions={husbandVisits}
                    selectedSurgicalVisit={selectedHusbandSurgicalVisit}
                    onSurgicalVisitChange={setSelectedHusbandSurgicalVisit}
                    onImportSurgicalHistory={handleImportHusbandSurgicalHistory}
                    importingSurgicalHistory={importingHusbandSurgicalHistory}
                    visitLabel="Visit"
                    procedureHistories={storedProcedureHistories}
                    procedureId={procedureId}
                    contactId={spouseInfo?.id}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    surgicalHistoryOptions={surgicalHistoryOptions}
                    onSaved={() => {
                      // Refresh procedure histories after edit/delete
                      api.ivf.getInitiationData(procedureId).then(res => {
                        if (res?.data?.procedureHistories) {
                          setStoredProcedureHistories(res.data.procedureHistories);
                          // Update displayed surgical histories
                          const updatedHusbandSurgicals = extractProcedureSurgicalHistory(
                            res.data.procedureHistories,
                            spouseInfo.id
                          );
                          setHusbandSurgicalHistory(updatedHusbandSurgicals);
                        }
                      });
                    }}
                  />
                </SectionWrapper>
                <SectionWrapper title="Semen Analysis" icon={ScienceIcon}>
                  <SemenAnalysisSection
                    semenHistory={husbandSemenAnalysis}
                    semenAnalysisOptions={
                      maleInvestigationParams?.param_group_members?.find(
                        m => m.slug === 'male-investigation-params-semen-analysis'
                      )?.param_group_member_options || []
                    }
                    maleInvestigationParams={maleInvestigationParams}
                    spouseInfo={spouseInfo}
                    canSave={canSave}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onSaved={handleSemenAnalysisSaved}
                  />
                  <DFISection
                    dfiHistory={husbandDfiHistory}
                    maleInvestigationParams={maleInvestigationParams}
                    spouseInfo={spouseInfo}
                    canSave={canSave}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onSaved={handleDfiSaved}
                  />
                </SectionWrapper>
                <SectionWrapper title="Blood Tests" icon={BloodtypeIcon}>
                  <SpouseBloodTestsSection
                    bloodParams={bloodParams}
                    spouseInfo={spouseInfo}
                    existingHistories={husbandContactHistories}
                    canSave={canSave}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onSaved={handleSpouseBloodSaved}
                  />
                </SectionWrapper>
              </Box>
            )}

            {/* ===== DONOR SECTION ===== */}
            {/* SELF sub-type has no donor. OD→Female, DS→Male, DET→both */}
            {shouldShowDonorSection && (
              <Box mb={3}>
                {/* OD – Oocyte Donation: Female donor only */}
                {procedureData.procedure_sub_type === 'OD' && (
                  <>
                    <SectionWrapper title="Donor (Female)" icon={VolunteerActivismIcon} defaultExpanded={true}>
                      <DonorSection
                        procedureId={procedureId}
                        donorType="F"
                        canSave={canSave}
                        canEdit={canEdit}
                        onDonorChange={handleDonorFemaleChange}
                      />
                    </SectionWrapper>
                    {donorFemaleContactId && (
                      <>
                        <SectionWrapper title="Donor Medical History" icon={HistoryIcon}>
                          <DonorMedicalHistorySection
                            contactId={donorFemaleContactId}
                            donorInfo={donorFemaleInfo}
                            genericHistory={donorFemaleGenericHistory}
                            surgicalHistory={donorFemaleSurgicalHistory}
                            procedureId={procedureId}
                            procedureHistories={storedProcedureHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => {
                              // Refresh procedure histories first
                              api.ivf.getInitiationData(procedureId).then(res => {
                                if (res?.data?.procedureHistories) {
                                  setStoredProcedureHistories(res.data.procedureHistories);
                                  // Update displayed surgical histories
                                  const updatedDonorFemaleSurgicals = extractProcedureSurgicalHistory(
                                    res.data.procedureHistories,
                                    donorFemaleContactId
                                  );
                                  setDonorFemaleSurgicalHistory(updatedDonorFemaleSurgicals);
                                }
                              });
                            }}
                          />
                        </SectionWrapper>
                        {(donorFemaleObstetricHistory || donorFemaleMiscarriageHistories.length > 0) && (
                          <SectionWrapper title="Donor Obstetric History" icon={PregnantWomanIcon} collapsible={false}>
                            <ObstetricHistoryDisplay
                              obstetricHistory={donorFemaleObstetricHistory}
                              miscarriageHistories={donorFemaleMiscarriageHistories}
                            />
                          </SectionWrapper>
                        )}
                        {donorFemaleMenstrualHistories.length > 0 && (
                          <SectionWrapper title="Donor Menstrual History" icon={WaterDropIcon}>
                            <MenstrualHistoryDisplay menstrualHistory={donorFemaleMenstrualHistories} />
                          </SectionWrapper>
                        )}
                        <SectionWrapper title="Donor Blood Tests" icon={BiotechIcon}>
                          <SpouseBloodTestsSection
                            bloodParams={bloodParams}
                            spouseInfo={{ id: donorFemaleContactId }}
                            existingHistories={donorFemaleContactHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorFemaleChange(donorFemaleContactId)}
                          />
                        </SectionWrapper>
                      </>
                    )}
                  </>
                )}

                {/* DS – Donor Sperm: Male donor only */}
                {procedureData.procedure_sub_type === 'DS' && (
                  <>
                    <SectionWrapper title="Donor (Male)" icon={VolunteerActivismIcon} defaultExpanded={true}>
                      <DonorSection
                        procedureId={procedureId}
                        donorType="M"
                        canSave={canSave}
                        canEdit={canEdit}
                        onDonorChange={handleDonorMaleChange}
                      />
                    </SectionWrapper>
                    {donorMaleContactId && (
                      <>
                        <SectionWrapper title="Donor Medical History" icon={HistoryIcon}>
                          <DonorMedicalHistorySection
                            contactId={donorMaleContactId}
                            donorInfo={donorMaleInfo}
                            genericHistory={donorMaleGenericHistory}
                            surgicalHistory={donorMaleSurgicalHistory}
                            procedureId={procedureId}
                            procedureHistories={storedProcedureHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => {
                              // Refresh procedure histories first
                              api.ivf.getInitiationData(procedureId).then(res => {
                                if (res?.data?.procedureHistories) {
                                  setStoredProcedureHistories(res.data.procedureHistories);
                                  // Update displayed surgical histories
                                  const updatedDonorMaleSurgicals = extractProcedureSurgicalHistory(
                                    res.data.procedureHistories,
                                    donorMaleContactId
                                  );
                                  setDonorMaleSurgicalHistory(updatedDonorMaleSurgicals);
                                }
                              });
                            }}
                          />
                        </SectionWrapper>
                        <SectionWrapper title="Semen Analysis" icon={ScienceIcon}>
                          <SemenAnalysisSection
                            semenHistory={donorMaleSemenAnalysis}
                            semenAnalysisOptions={
                              maleInvestigationParams?.param_group_members?.find(
                                m => m.slug === 'male-investigation-params-semen-analysis'
                              )?.param_group_member_options || []
                            }
                            maleInvestigationParams={maleInvestigationParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                          <DFISection
                            dfiHistory={donorMaleDfiHistory}
                            maleInvestigationParams={maleInvestigationParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                        </SectionWrapper>
                        <SectionWrapper title="Donor Blood Tests" icon={BloodtypeIcon}>
                          <SpouseBloodTestsSection
                            bloodParams={bloodParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            existingHistories={donorMaleContactHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                        </SectionWrapper>
                      </>
                    )}
                  </>
                )}

                {/* DET – Double Embryo Transfer: both Male and Female donors */}
                {procedureData.procedure_sub_type === 'DET' && (
                  <>
                    <Box mb={2}>
                      <SectionWrapper title="Donor (Female)" icon={VolunteerActivismIcon} defaultExpanded={true}>
                        <DonorSection
                          procedureId={procedureId}
                          donorType="F"
                          canSave={canSave}
                          canEdit={canEdit}
                          onDonorChange={handleDonorFemaleChange}
                        />
                      </SectionWrapper>
                      {donorFemaleContactId && (
                        <>
                          <SectionWrapper title="Donor Medical History" icon={HistoryIcon}>
                            <DonorMedicalHistorySection
                              contactId={donorFemaleContactId}
                              donorInfo={donorFemaleInfo}
                              genericHistory={donorFemaleGenericHistory}
                              surgicalHistory={donorFemaleSurgicalHistory}
                              procedureId={procedureId}
                              procedureHistories={storedProcedureHistories}
                              canSave={canSave}
                              canEdit={canEdit}
                              canDelete={canDelete}
                              onSaved={() => {
                                // Refresh procedure histories first
                                api.ivf.getInitiationData(procedureId).then(res => {
                                  if (res?.data?.procedureHistories) {
                                    setStoredProcedureHistories(res.data.procedureHistories);
                                    // Update displayed surgical histories
                                    const updatedDonorFemaleSurgicals = extractProcedureSurgicalHistory(
                                      res.data.procedureHistories,
                                      donorFemaleContactId
                                    );
                                    setDonorFemaleSurgicalHistory(updatedDonorFemaleSurgicals);
                                  }
                                });
                              }}
                            />
                          </SectionWrapper>
                          {(donorFemaleObstetricHistory || donorFemaleMiscarriageHistories.length > 0) && (
                            <SectionWrapper title="Donor Obstetric History" icon={PregnantWomanIcon} collapsible={false}>
                              <ObstetricHistoryDisplay
                                obstetricHistory={donorFemaleObstetricHistory}
                                miscarriageHistories={donorFemaleMiscarriageHistories}
                              />
                            </SectionWrapper>
                          )}
                          {donorFemaleMenstrualHistories.length > 0 && (
                            <SectionWrapper title="Donor Menstrual History" icon={WaterDropIcon}>
                              <MenstrualHistoryDisplay menstrualHistory={donorFemaleMenstrualHistories} />
                            </SectionWrapper>
                          )}
                          <SectionWrapper title="Donor Blood Tests" icon={BiotechIcon}>
                            <SpouseBloodTestsSection
                              bloodParams={bloodParams}
                              spouseInfo={{ id: donorFemaleContactId }}
                              existingHistories={donorFemaleContactHistories}
                              canSave={canSave}
                              canEdit={canEdit}
                              canDelete={canDelete}
                              onSaved={() => handleDonorFemaleChange(donorFemaleContactId)}
                            />
                          </SectionWrapper>
                        </>
                      )}
                    </Box>
                    <SectionWrapper title="Donor (Male)" icon={VolunteerActivismIcon} defaultExpanded={true}>
                      <DonorSection
                        procedureId={procedureId}
                        donorType="M"
                        canSave={canSave}
                        canEdit={canEdit}
                        onDonorChange={handleDonorMaleChange}
                      />
                    </SectionWrapper>
                    {donorMaleContactId && (
                      <>
                        <SectionWrapper title="Donor Medical History" icon={HistoryIcon}>
                          <DonorMedicalHistorySection
                            contactId={donorMaleContactId}
                            donorInfo={donorMaleInfo}
                            genericHistory={donorMaleGenericHistory}
                            surgicalHistory={donorMaleSurgicalHistory}
                            procedureId={procedureId}
                            procedureHistories={storedProcedureHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => {
                              // Refresh procedure histories first
                              api.ivf.getInitiationData(procedureId).then(res => {
                                if (res?.data?.procedureHistories) {
                                  setStoredProcedureHistories(res.data.procedureHistories);
                                  // Update displayed surgical histories
                                  const updatedDonorMaleSurgicals = extractProcedureSurgicalHistory(
                                    res.data.procedureHistories,
                                    donorMaleContactId
                                  );
                                  setDonorMaleSurgicalHistory(updatedDonorMaleSurgicals);
                                }
                              });
                            }}
                          />
                        </SectionWrapper>
                        <SectionWrapper title="Semen Analysis" icon={ScienceIcon}>
                          <SemenAnalysisSection
                            semenHistory={donorMaleSemenAnalysis}
                            semenAnalysisOptions={
                              maleInvestigationParams?.param_group_members?.find(
                                m => m.slug === 'male-investigation-params-semen-analysis'
                              )?.param_group_member_options || []
                            }
                            maleInvestigationParams={maleInvestigationParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                          <DFISection
                            dfiHistory={donorMaleDfiHistory}
                            maleInvestigationParams={maleInvestigationParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                        </SectionWrapper>
                        <SectionWrapper title="Donor Blood Tests" icon={BloodtypeIcon}>
                          <SpouseBloodTestsSection
                            bloodParams={bloodParams}
                            spouseInfo={{ id: donorMaleContactId }}
                            existingHistories={donorMaleContactHistories}
                            canSave={canSave}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onSaved={() => handleDonorMaleChange(donorMaleContactId)}
                          />
                        </SectionWrapper>
                      </>
                    )}
                  </>
                )}
              </Box>
            )}


            {/* ===== PLAN & CAUSE OF INFERTILITY ===== */}
            <Box mb={3}>
              <SectionWrapper title="Plan &amp; Cause of Infertility" icon={EventNoteIcon} defaultExpanded={true}>
                <PlanAndCauseSection
                  procedureId={procedureId}
                  existingDetail={procedureDetail}
                  canSave={canSave}
                  onDetailSaved={(detail) => setLatestDetail(detail)}
                />
              </SectionWrapper>
            </Box>

          </form>
        </ProcedureInitiationContainer>

        {/* Print Button — outside the printable ref, so it is never included in output */}
        <Box display="flex" justifyContent="flex-end" mt={2} mb={2}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: primaryColor,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '14px',
              borderRadius: '8px',
              px: 4,
              py: 1.2,
              boxShadow: `0 4px 14px ${primaryColor}60`,
              letterSpacing: '0.3px',
              '&:hover': {
                backgroundColor: primaryHoverColor,
                boxShadow: `0 6px 18px ${primaryColor}80`,
              },
            }}
          >
            Print
          </Button>
        </Box>

        {/* Import Dialogs */}
        <ImportSurgicalHistoryDialog
          open={showWifeSurgicalImportDialog}
          onClose={() => setShowWifeSurgicalImportDialog(false)}
          contactId={patientInfo?.id}
          visitOptions={visits}
          sectionLabel="Wife"
          onImport={handleWifeSurgicalImport}
          primaryColor={primaryColor}
        />

        <ImportSurgicalHistoryDialog
          open={showHusbandSurgicalImportDialog}
          onClose={() => setShowHusbandSurgicalImportDialog(false)}
          contactId={spouseInfo?.id}
          visitOptions={husbandVisits}
          sectionLabel="Husband"
          onImport={handleHusbandSurgicalImport}
          primaryColor={primaryColor}
        />

        {/* Hidden off-screen dedicated print view — react-to-print clones this */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px' }}>
          <ProcedureInitiationPrint
            ref={printRef}
            patientInfo={patientInfo}
            spouseInfo={spouseInfo}
            procedureData={procedureData}
            obstetricHistory={obstetricHistory}
            miscarriageHistories={miscarriageHistories}
            menstrualHistories={menstrualHistories}
            genericHistory={genericHistory}
            surgicalHistory={surgicalHistory}
            bloodGroup={bloodGroup}
            procedureDetail={latestDetail || procedureDetail}
            husbandGenericHistory={husbandGenericHistory}
            husbandSurgicalHistory={husbandSurgicalHistory}
            husbandSemenAnalysis={husbandSemenAnalysis}
            husbandContactHistories={husbandContactHistories}
            storedProcedureHistories={storedProcedureHistories}
            bloodParams={bloodParams}
            storedProcedureUsgs={storedProcedureUsgs}
            wifeVitals={getValues()}
            husbandVitals={hGetValues()}
            donorFemaleInfo={donorFemaleInfo}
            donorFemaleGenericHistory={donorFemaleGenericHistory}
            donorFemaleSurgicalHistory={donorFemaleSurgicalHistory}
            donorFemaleContactHistories={donorFemaleContactHistories}
            donorMaleInfo={donorMaleInfo}
            donorMaleGenericHistory={donorMaleGenericHistory}
            donorMaleSurgicalHistory={donorMaleSurgicalHistory}
            donorMaleContactHistories={donorMaleContactHistories}
            donorMaleSemenAnalysis={donorMaleSemenAnalysis}
          />
        </div>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ProcedureInitiation;
